/** Grenada locations: six mainland parishes plus Carriacou and Petite Martinique (used in filters and URLs). */
export const GRENADA_PARISHES = [
  'Saint Andrew',
  'Saint David',
  'Saint George',
  'Saint John',
  'Saint Mark',
  'Saint Patrick',
  'Carriacou',
  'Petite Martinique',
] as const;

export type GrenadaParish = (typeof GRENADA_PARISHES)[number];

/** Map stored parish strings (legacy labels, casing) to a canonical option value. */
export function normalizeGrenadaParish(value: string | null | undefined): GrenadaParish | '' {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return '';

  if ((GRENADA_PARISHES as readonly string[]).includes(trimmed)) {
    return trimmed as GrenadaParish;
  }

  const lower = trimmed.toLowerCase();
  const aliases: Record<string, GrenadaParish> = {
    'st. george': 'Saint George',
    'st george': 'Saint George',
    'st. andrew': 'Saint Andrew',
    'st andrew': 'Saint Andrew',
    'st. david': 'Saint David',
    'st david': 'Saint David',
    'st. john': 'Saint John',
    'st john': 'Saint John',
    'st. mark': 'Saint Mark',
    'st mark': 'Saint Mark',
    'st. patrick': 'Saint Patrick',
    'st patrick': 'Saint Patrick',
    'st.patrick': 'Saint Patrick',
    'stpatrick': 'Saint Patrick',
    'petit martinique': 'Petite Martinique',
    'petite martinique': 'Petite Martinique',
  };

  if (aliases[lower]) return aliases[lower];

  const byLower = GRENADA_PARISHES.find((p) => p.toLowerCase() === lower);
  return byLower ?? '';
}

/** URL segment for a parish name, e.g. Petite Martinique → petite-martinique */
export function parishToSlug(parish: string): string {
  return parish
    .toLowerCase()
    .replace(/['.]/g, '')
    .replace(/\s+/g, '-')
    .trim();
}

export function slugToParishLabel(slug: string): GrenadaParish | string {
  const found = GRENADA_PARISHES.find((p) => parishToSlug(p) === slug);
  return found ?? slug.replace(/-/g, ' ');
}

/** Dependencies use /grenada/{parish}/business/{slug}; main island uses /grenada/business/{slug}. */
export const PARISH_BUSINESS_URL_PREFIX: readonly GrenadaParish[] = [
  'Carriacou',
  'Petite Martinique',
];

export function parishUsesBusinessUrlPrefix(parish: GrenadaParish | ''): boolean {
  return parish !== '' && (PARISH_BUSINESS_URL_PREFIX as readonly string[]).includes(parish);
}

/** Parish slug for dependency-island listing URLs (business, product, classified, property). */
export function islandListingUrlParishSlug(parish: string | null | undefined): string | null {
  const normalized = normalizeGrenadaParish(parish);
  if (!parishUsesBusinessUrlPrefix(normalized)) return null;
  return parishToSlug(normalized);
}

/** @deprecated Use islandListingUrlParishSlug */
export const businessUrlParishSlug = islandListingUrlParishSlug;
